import React from "react";
import {
  OrganisationQuoteTemplatesFilters,
  OrganisationQuoteTemplatesSorting,
  TemplateSearchFiltersInput,
} from "../../../../clients/graphqlTypes.js";
import { useFiltersAndPagination } from "../../../commons/filters/useFiltersAndPagination.js";
import { CollectionView } from "../../../commons/hooks/useCollectionView.js";
import { useStateWithUrlParam } from "../../../commons/hooks/useStateWithUrlParam.js";
import { PAGE_LIST_RESULTS_PER_PAGE } from "../../../constants.js";
import { DEFAULT_SORTING } from "./TemplatesQuoteFilterModal.js";
import { usePaginationParamsInState } from "../../../commons/pagination/usePaginationParams.js";
import { TemplateFiltersFormValues } from "../filters/TemplateFilterFields.js";

interface UseTemplatesQueryVariables<
  Filters extends
    | OrganisationQuoteTemplatesFilters
    | TemplateSearchFiltersInput,
> {
  initialFilters: Filters;
  initialSorting?: OrganisationQuoteTemplatesSorting[];
  initialSearchTerm?: string;
  perPageDefaultAmount?: number;
}

export const useTemplatesQueryVariablesInUrl = <
  Filters extends
    | OrganisationQuoteTemplatesFilters
    | TemplateSearchFiltersInput,
>({
  initialFilters,
  initialSorting,
  initialSearchTerm,
  perPageDefaultAmount = PAGE_LIST_RESULTS_PER_PAGE,
  searchUrlParam = "search",
  pageUrlParam = "page",
  perPageUrlParam = "pageSize",
  filtersUrlParam = "filters",
  sortingsUrlParam = "sortings",
}: UseTemplatesQueryVariables<Filters> & {
  searchUrlParam?: string;
  pageUrlParam?: string;
  perPageUrlParam?: string;
  filtersUrlParam?: string;
  sortingsUrlParam?: string;
}) => {
  const {
    limit,
    offset,
    paginationModel,
    setPaginationModel,
    filters,
    setFilters,
    resetFilters,
    sorting,
    setSorting,
    toRemoveParams,
  } = useFiltersAndPagination<OrganisationQuoteTemplatesSorting, Filters>(
    initialFilters,
    initialSorting ?? DEFAULT_SORTING,
    perPageDefaultAmount,
    pageUrlParam,
    perPageUrlParam,
    filtersUrlParam,
    sortingsUrlParam
  );

  const [searchTerm, setSearchTerm] = useStateWithUrlParam<string>(
    searchUrlParam,
    initialSearchTerm ?? "",
    toRemoveParams
  );

  const QUERY_BASE_VARIABLES = {
    offset,
    limit,
    sorting: initialSorting ?? DEFAULT_SORTING,
  };

  return {
    limit,
    offset,
    paginationModel,
    setPaginationModel,
    filters,
    setFilters,
    resetFilters,
    sorting,
    setSorting,
    queryBaseVariables: QUERY_BASE_VARIABLES,
    toRemoveParams,
    searchTerm,
    setSearchTerm,
  };
};

/**
 * The picker's template query state, owned by the *modal* rather than by the
 * results component, so it survives the component being unmounted when the
 * user switches to the Products tab and back — which the placeholder-fill
 * flow does routinely, since its own "Build it from products" action
 * switches tabs. Without this, coming back re-seeded from scratch and threw
 * away the user's template-type edits and any filters they had cleared.
 *
 * Only the destination (placeholder-fill) flow needs it: every other caller
 * keeps this state in the URL, which already survives a remount.
 */
export const useTemplatesPickerQueryState = ({
  hasDestination,
  initialTemplateFilters,
  hasSeedPropertySortings,
  initialSearchTerm,
}: {
  hasDestination: boolean;
  initialTemplateFilters?: TemplateFiltersFormValues;
  hasSeedPropertySortings: boolean;
  initialSearchTerm: string;
}) => {
  // Both hooks run unconditionally (rules of hooks); only one result is used.
  // Reset target is the type only, so "Clear quote filters" keeps it —
  // property filters are layered on top once, after mount, below.
  const urlVars = useTemplatesQueryVariablesInUrl<TemplateSearchFiltersInput>({
    initialFilters: {},
    initialSearchTerm,
    searchUrlParam: "templatesSelectSearch",
    pageUrlParam: "templatesSelectPage",
    perPageUrlParam: "templatesSelectPageSize",
    filtersUrlParam: "templatesSelectFilters",
    sortingsUrlParam: "templatesSelectSortings",
  });
  const stateVars =
    useTemplatesQueryVariablesInState<TemplateSearchFiltersInput>({
      initialFilters: hasDestination
        ? { templateTypeIds: initialTemplateFilters?.templateTypeIds ?? null }
        : {},
      // Start with no column sorting when the placeholder seeds property
      // sortings, or they'd never take effect: the query appends them after
      // the column sorts, and the default `createdAt desc` is close to
      // unique, so a seeded sorting left in second place is a tie-breaker
      // that never breaks a tie. Picking a column later still wins — it just
      // becomes the primary sort ahead of them, which is what the user asked
      // for at that point.
      initialSorting:
        hasDestination && hasSeedPropertySortings ? [] : undefined,
      initialSearchTerm,
    });

  const vars = hasDestination ? stateVars : urlVars;
  const { setFilters } = vars;

  const didSeedTemplateFiltersRef = React.useRef(false);
  React.useEffect(() => {
    if (
      hasDestination &&
      initialTemplateFilters &&
      !didSeedTemplateFiltersRef.current
    ) {
      didSeedTemplateFiltersRef.current = true;
      setFilters(current => ({ ...current, ...initialTemplateFilters }));
    }
    // Seed once on mount only — this must not re-fire on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return vars;
};

export type TemplatesPickerQueryState = ReturnType<
  typeof useTemplatesPickerQueryState
>;

export const useTemplatesQueryVariablesInState = <
  Filters extends
    | OrganisationQuoteTemplatesFilters
    | TemplateSearchFiltersInput,
>({
  initialFilters,
  initialSorting,
  initialSearchTerm,
  perPageDefaultAmount = PAGE_LIST_RESULTS_PER_PAGE,
}: UseTemplatesQueryVariables<Filters>) => {
  const { offset, limit, paginationModel, setPaginationModel } =
    usePaginationParamsInState({ perPageDefaultAmount });

  const [filters, setFilters] = React.useState<Filters>(initialFilters);
  const resetFilters = React.useCallback(() => {
    setFilters(initialFilters);
  }, [initialFilters, setFilters]);

  const [sorting, setSorting] = React.useState<
    OrganisationQuoteTemplatesSorting[]
  >(initialSorting ?? DEFAULT_SORTING);

  const [searchTerm, setSearchTerm] = React.useState<string>(
    initialSearchTerm ?? ""
  );

  const QUERY_BASE_VARIABLES = {
    offset,
    limit,
    sorting: initialSorting ?? DEFAULT_SORTING,
  };

  return {
    limit,
    offset,
    paginationModel,
    setPaginationModel,
    filters,
    setFilters,
    resetFilters,
    sorting,
    setSorting,
    queryBaseVariables: QUERY_BASE_VARIABLES,
    searchTerm,
    setSearchTerm,
  };
};
